import { type InlineDescriptionV2_organization$key } from '#app/__generated__/relay/InlineDescriptionV2_organization.graphql.js';
import { useMatchMedia } from '#app/hooks/useMatchMedia.js';
import { parseMediaQueryForMatchMedia } from '#app/utilities/parseMediaQueryForMatchMedia.js';
import {
  DescriptionContainer,
  DescriptionText,
  ViewMoreButton,
} from './InlineDescription.styles.js';
import { graphql, useFragment } from '@contra/relay';
import { useTheme } from 'styled-components';

const MAX_DEFAULT_DESCRIPTION_LENGTH = 280;

const resolveDescription = ({
  description,
  isMediumScreen,
  isViewingMore,
}: {
  readonly description: null | string;
  readonly isMediumScreen: boolean;
  readonly isViewingMore: boolean;
}) => {
  if (!description) {
    return '';
  }

  if (isViewingMore) {
    return description;
  }

  const maxLength = isMediumScreen
    ? MAX_DEFAULT_DESCRIPTION_LENGTH
    : Math.floor(MAX_DEFAULT_DESCRIPTION_LENGTH / 3);
  if (description.length > maxLength) {
    return description.slice(0, maxLength);
  }

  return description;
};

export const InlineDescriptionV2 = ({
  hideViewMore,
  isViewingMore,
  onClickViewMore,
  organizationRef,
}: {
  readonly hideViewMore: boolean;
  readonly isViewingMore: boolean;
  readonly onClickViewMore: () => void;
  readonly organizationRef: InlineDescriptionV2_organization$key;
}) => {
  const data = useFragment<InlineDescriptionV2_organization$key>(
    graphql`
      fragment InlineDescriptionV2_organization on Organization {
        description
      }
    `,
    organizationRef,
  );

  const theme = useTheme();
  const isMediumScreen = useMatchMedia(
    parseMediaQueryForMatchMedia(theme.mediaQueries.md),
  );
  const { description } = data;
  const resolvedDescription = resolveDescription({
    description,
    isMediumScreen,
    isViewingMore,
  });

  return (
    <DescriptionContainer>
      <DescriptionText>
        {resolvedDescription}
        {isViewingMore || hideViewMore ? null : (
          <ViewMoreButton
            aria-label="view more"
            onClick={onClickViewMore}
          >
            view more...
          </ViewMoreButton>
        )}
      </DescriptionText>
    </DescriptionContainer>
  );
};
